import { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import {
  Dialog,
  DialogPanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon, PhoneIcon } from '@heroicons/react/20/solid'
import { products } from '../data/products'
import { services } from '../data/services'

const callsToAction = [{ name: 'Contact sales', href: '/#contact', icon: PhoneIcon }]

export default function Header() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<string | null>(null)

  useEffect(() => {
    if (pathname !== '/') {
      setActiveSection(null)
      return
    }
    const ids = ['products', 'services', 'company', 'contact']
    const observers: IntersectionObserver[] = []
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id) },
        { threshold: 0.3 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((obs) => obs.disconnect())
  }, [pathname])

  const handleNavClick = (href: string, close?: () => void) => {
    if (close) close()
    setMobileMenuOpen(false)
    if (href.startsWith('/#')) {
      const id = href.slice(2)
      setActiveSection(id)
      if (window.location.pathname !== '/') {
        navigate('/')
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
        }, 100)
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      navigate(href)
    }
  }

  const navClass = (section: string) =>
    `flex cursor-pointer items-center gap-x-1 text-sm font-semibold transition-colors focus:outline-none ${
      pathname === '/' && activeSection === section
        ? 'text-indigo-600'
        : 'text-gray-900 hover:text-indigo-600'
    }`

  const linkClass = (path: string) =>
    `text-sm font-semibold transition-colors ${
      pathname === path ? 'text-indigo-600' : 'text-gray-900 hover:text-indigo-600'
    }`

  return (
    <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100/60 sticky top-0 z-50">
      <nav
        aria-label="Global"
        className="mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-8"
      >
        <div className="flex items-center lg:flex-1">
          <Link to="/" className="flex items-center space-x-3">
            <img src="/logo.png" alt="Accezz Minds logo" className="h-10 w-auto" />
            <span className="text-lg font-semibold text-purple-700">Accezz Minds</span>
          </Link>
        </div>

        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="size-6" aria-hidden="true" />
          </button>
        </div>

        <PopoverGroup className="hidden lg:flex lg:gap-x-10">
          {/* Products dropdown */}
          <Popover className="relative">
            <PopoverButton className={navClass('products')}>
              Products
              <ChevronDownIcon className="size-4 flex-none text-gray-400" aria-hidden="true" />
            </PopoverButton>
            <PopoverPanel
              transition
              className="absolute left-1/2 z-50 mt-3 w-screen max-w-md -translate-x-1/2 overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[enter]:ease-out data-[leave]:duration-150 data-[leave]:ease-in"
            >
              {({ close }) => (
                <>
                  <div className="p-4">
                    {products.map((item) => (
                      <div
                        key={item.id}
                        className="group relative flex items-center gap-x-6 rounded-lg p-4 text-sm hover:bg-gray-50"
                      >
                        <div className="flex size-10 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                          <item.icon
                            className="size-6 text-gray-600 group-hover:text-indigo-600"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="flex-auto">
                          <a
                            href="/#products"
                            onClick={(e) => {
                              e.preventDefault()
                              handleNavClick('/#products', close)
                            }}
                            className="block font-semibold text-gray-900"
                          >
                            {item.name}
                            <span className="absolute inset-0" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-gray-50">
                    {callsToAction.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        onClick={(e) => {
                          e.preventDefault()
                          handleNavClick(item.href, close)
                        }}
                        className="flex items-center justify-center gap-x-2.5 p-3 text-sm font-semibold text-gray-900 hover:bg-gray-100"
                      >
                        <item.icon className="size-5 flex-none text-gray-400" aria-hidden="true" />
                        {item.name}
                      </a>
                    ))}
                  </div>
                </>
              )}
            </PopoverPanel>
          </Popover>

          {/* Services dropdown */}
          <Popover className="relative">
            <PopoverButton className={navClass('services')}>
              Services
              <ChevronDownIcon className="size-4 flex-none text-gray-400" aria-hidden="true" />
            </PopoverButton>
            <PopoverPanel
              transition
              className="absolute left-1/2 z-50 mt-3 w-screen max-w-md -translate-x-1/2 overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[enter]:ease-out data-[leave]:duration-150 data-[leave]:ease-in"
            >
              {({ close }) => (
                <>
                  <div className="p-4">
                    {services.map((item) => (
                      <div
                        key={item.id}
                        className="group relative flex items-center gap-x-6 rounded-lg p-4 text-sm hover:bg-gray-50"
                      >
                        <div className="flex size-10 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                          <item.icon
                            className="size-6 text-gray-600 group-hover:text-indigo-600"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="flex-auto">
                          <a
                            href={item.href}
                            onClick={(e) => {
                              e.preventDefault()
                              handleNavClick(item.href, close)
                            }}
                            className="block font-semibold text-gray-900"
                          >
                            {item.name}
                            <span className="absolute inset-0" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-gray-50">
                    {callsToAction.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        onClick={(e) => {
                          e.preventDefault()
                          handleNavClick(item.href, close)
                        }}
                        className="flex items-center justify-center gap-x-2.5 p-3 text-sm font-semibold text-gray-900 hover:bg-gray-100"
                      >
                        <item.icon className="size-5 flex-none text-gray-400" aria-hidden="true" />
                        {item.name}
                      </a>
                    ))}
                  </div>
                </>
              )}
            </PopoverPanel>
          </Popover>

          <Link to="/tools" className={linkClass('/tools')}>
            Tools
          </Link>

          <a
            href="/#company"
            onClick={(e) => {
              e.preventDefault()
              handleNavClick('/#company')
            }}
            className={navClass('company')}
          >
            Company
          </a>

          <a
            href="/#contact"
            onClick={(e) => {
              e.preventDefault()
              handleNavClick('/#contact')
            }}
            className={navClass('contact')}
          >
            Contact
          </a>
        </PopoverGroup>

        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <a
            href="https://app.accezzminds.com/login"
            className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors"
          >
            Login
          </a>
        </div>
      </nav>

      {/* Mobile menu */}
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-10" />
        <DialogPanel className="fixed inset-y-0 right-0 z-10 flex w-full flex-col justify-between overflow-y-auto bg-white sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-3">
                <img src="/logo.png" alt="logo" className="h-10 w-auto" />
                <span className="text-lg font-semibold text-purple-700">Accezz Minds</span>
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="-m-2.5 rounded-md p-2.5 text-gray-700"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="size-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-1 py-6">
                  <p className="px-3 text-xs font-semibold uppercase text-gray-400 tracking-wider mb-2">
                    Products
                  </p>
                  {products.map((item) => (
                    <a
                      key={item.id}
                      href="/#products"
                      onClick={(e) => {
                        e.preventDefault()
                        handleNavClick('/#products')
                      }}
                      className="group -mx-3 flex items-center gap-x-6 rounded-lg p-3 text-base font-semibold text-gray-900 hover:bg-gray-50"
                    >
                      <div className="flex size-10 flex-none items-center justify-center rounded-lg bg-gray-50">
                        <item.icon
                          className="size-6 text-gray-600 group-hover:text-indigo-600"
                          aria-hidden="true"
                        />
                      </div>
                      {item.name}
                    </a>
                  ))}
                </div>
                <div className="space-y-1 py-6">
                  <p className="px-3 text-xs font-semibold uppercase text-gray-400 tracking-wider mb-2">
                    Services
                  </p>
                  {services.map((item) => (
                    <a
                      key={item.id}
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault()
                        handleNavClick(item.href)
                      }}
                      className="group -mx-3 flex items-center gap-x-6 rounded-lg p-3 text-base font-semibold text-gray-900 hover:bg-gray-50"
                    >
                      <div className="flex size-10 flex-none items-center justify-center rounded-lg bg-gray-50">
                        <item.icon
                          className="size-6 text-gray-600 group-hover:text-indigo-600"
                          aria-hidden="true"
                        />
                      </div>
                      {item.name}
                    </a>
                  ))}
                </div>
                <div className="space-y-2 py-6">
                  <Link
                    to="/tools"
                    onClick={() => setMobileMenuOpen(false)}
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    Tools
                  </Link>
                  <a
                    href="/#company"
                    onClick={(e) => {
                      e.preventDefault()
                      handleNavClick('/#company')
                    }}
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    Company
                  </a>
                  <a
                    href="/#contact"
                    onClick={(e) => {
                      e.preventDefault()
                      handleNavClick('/#contact')
                    }}
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    Contact
                  </a>
                </div>
                <div className="py-6">
                  <a
                    href="https://app.accezzminds.com/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg bg-indigo-600 px-4 py-2.5 text-center text-base font-semibold text-white hover:bg-indigo-500"
                  >
                    Login
                  </a>
                </div>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  )
}
